import {
  documentToReactComponents,
  type Options,
} from '@contentful/rich-text-react-renderer'
import {
  BLOCKS,
  type Document,
  INLINES,
  MARKS,
  type Text,
} from '@contentful/rich-text-types'
import { embeddedAsset } from '@render-node/embedded-asset'
import { entryResolver } from '@render-node/entry-resolver'
import { renderText } from '@render-text/index'
import { ErrorBoundary } from '@ui/error-boundary'
import React, {
  type FC,
  Fragment,
  memo,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react'

import { CUSTOMS, type RichTextOptions, type RichTextType } from './types'

type Props = {
  content?: RichTextType
  options: RichTextOptions
  className?: string
}

const getOptions = (references: readonly any[] | undefined) => {
  const referencesBlockMap = new Map()
  if (references) {
    for (const reference of references) {
      referencesBlockMap.set(reference.contentful_id, reference)
    }
  }

  return referencesBlockMap
}

const initDefaultValue = (keys: Record<string, string>, initCallback: any) =>
  Object.values(keys).reduce(
    (previousValue: Record<string, unknown>, currentValue: string) => ({
      ...previousValue,
      [currentValue]: initCallback,
    }),
    {}
  )

const defaultOptions = {
  renderMark: initDefaultValue(MARKS, (text: Text) => text),
  renderNode: {
    ...initDefaultValue(CUSTOMS, () => <></>),
    ...initDefaultValue(BLOCKS, () => <></>),
    [BLOCKS.DOCUMENT]: (node: any, children: ReactNode) => children,
  },
  renderText,
}

const RichText: FC<Props> = memo(
  ({ content, options, ...props }) => {
    const { rich, references } = content || {}
    const json = useMemo<Document>(() => {
      try {
        return rich ? JSON.parse(rich) : ''
      } catch (error) {
        console.error(error)
      }

      return ''
    }, [content])

    if (json) {
      const newObjectOptions = getOptions(references)
      const computedOptions: Options = {
        ...defaultOptions,
        ...options,
        renderNode: {
          ...defaultOptions.renderNode,
          ...options.renderNode,
          [INLINES.ASSET_HYPERLINK]: entryResolver(
            newObjectOptions,
            options.renderNode[INLINES.ASSET_HYPERLINK]
          ),
          [BLOCKS.EMBEDDED_ASSET]: embeddedAsset(
            newObjectOptions,
            options.renderNode[BLOCKS.EMBEDDED_ASSET]
          ),
          [INLINES.ENTRY_HYPERLINK]: entryResolver(
            newObjectOptions,
            options.renderNode[INLINES.ENTRY_HYPERLINK]
          ),
          [INLINES.EMBEDDED_ENTRY]: entryResolver(
            newObjectOptions,
            options.renderNode[INLINES.EMBEDDED_ENTRY]
          ),
          [BLOCKS.EMBEDDED_ENTRY]: entryResolver(
            newObjectOptions,
            options.renderNode[BLOCKS.EMBEDDED_ENTRY]
          ),
        },
      }

      // TODO spread props to BLOCKS.DOCUMENT instead of div
      return (
        <ErrorBoundary>
          <div {...props}>
            {documentToReactComponents(json, computedOptions) as ReactElement}
          </div>
        </ErrorBoundary>
      )
    }

    return <></>
  },
  // Only re-render if content and children changes, ignore mapping on second re-render
  // mapping is a determinist immutable array
  (oldProps, newProps) => Object.is(oldProps.content, newProps.content)
)

export default RichText
